import { makeAutoObservable, reaction, runInAction } from 'mobx';
import type billingApiAgent from '../../services/api/billingApiAgent';
import type { TQuotaPackage } from '../../services/models/BillingModels';
import type { TQuotaBalance } from '../../services/models/QuotaModels';
import { resolveErrorMessage } from '../../utils/resolveErrorMessage';
import type CompanyFieldStore from '../companyField/companyFieldStore';

export interface IPurchaseStoreDependencies {
  /** The part of `billingApiAgent` a purchase uses: `QuotaPackages`. The register lookup is the company field's. */
  agent: Pick<typeof billingApiAgent, 'QuotaPackages'>;
  /** Where the balance a purchase answers with goes: the balance store's `accept`, wired in `createPurchaseStore`. */
  acceptBalance: (balance: TQuotaBalance) => void;
  /** The form's company field, with its own register lookup and clock: `createCompanyFieldStore`, wired in `createPurchaseStore`. */
  companyField: CompanyFieldStore;
  /** Makes the purchase form's idempotency key, a UUID: `uuid`'s v4, wired in `createPurchaseStore`. */
  newKey: () => string;
}

export const PURCHASE_FAILED_MESSAGE = 'The purchase could not be completed. Please try again.';

/**
 * What the author is told when a purchase was sent and no answer came back: it may well have been made,
 * and the backend finishes it. Not "try again": Buy stays available, and the form's key makes another
 * click the same purchase, but nothing here asks for one.
 */
export const PURCHASE_NOT_CONFIRMED_MESSAGE = 'Your order could not be confirmed. It will be checked, and the credits are added if it went through.';

/**
 * Whether the backend answered the purchase it was sent: every refusal it writes reaches the store with
 * its HTTP status. An error without one is a request whose answer never arrived.
 */
const wasAnswered = (error: unknown): boolean => typeof (error as { status?: unknown } | null)?.status === 'number';

export const FORM_UNAVAILABLE_MESSAGE = 'The quota packages could not be loaded. Please try again.';

export const ORDER_REGISTERED_MESSAGE = 'Your order is registered. The credits are added as soon as the invoice has been sent.';

export const INVALID_INVOICE_EMAIL_MESSAGE = 'Enter a valid email address.';

/** The shape an invoice email must have before the purchase is sent: something, an @, something, a dot, something, and no spaces. The backend checks it properly. */
const SIMPLE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What the confirmation shows once a purchase stands. */
export interface IPurchaseConfirmation {
  productName: string;
  credits: number;
  /** True when the order stands but the credits wait for the invoice to be handed on. */
  isPending: boolean;
  /** The backend's words for a pending order. Null when accepted: the form then says what was added and what the balance is. */
  message: string | null;
  /** The author's credits once the purchase stands, Monthly and Purchased together; null when the answer carried no balance. */
  totalCreditsAfter: number | null;
}

/**
 * Buying a quota package from the balance indicator: the purchase form, from opening it with the
 * packages on sale and the prefilled company, through one purchase, to the confirmation that carries
 * the new balance into the balance store.
 *
 * The company field takes an organisation number or a name. Nine digits with a valid check digit are
 * looked up in Brønnøysundregistrene at once; a name — at least one letter, two characters or more — is
 * searched for after a pause in typing and offered as suggestions. The purchase is made with the resolved company only —
 * the register's number and name, never what was typed — and the backend looks the number up once
 * more. A refusal — the backend's words, "try again shortly" included — is shown and the form can be
 * submitted again. Closing the form forgets it; nothing is sent by closing.
 *
 * A purchase is submitted once. The form makes an idempotency key as it opens and sends it with every
 * buy, so the backend answers a repeat with the purchase it made. The key is kept while the form is open,
 * and made anew only after the backend has answered a refusal, when buying again is a new purchase. An
 * answer that never arrives is not a refusal: the form says the order could not be confirmed and will be
 * checked, and a Buy clicked after it sends the same key.
 *
 * Once the owner has an accepted purchase, the backend says the company is locked (unless the person
 * signed in is staff): the field then holds that company, resolved, and ignores typing and picking. The
 * backend refuses another company all the same.
 *
 * The company field is a company field store of its own (`companyField`, rendered by `CompanyField`),
 * handed in by the composition root; the form reads what it holds from it.
 *
 * Imports no agent, no store and no React; the agent, the balance store and the company field are
 * ports, wired in `createPurchaseStore`.
 */
export default class PurchaseStore {
  isOpen = false;

  /** True while the packages and the prefill are on their way. */
  isLoading = false;

  /** Why the form could not be opened, or null. */
  loadFailureMessage: string | null = null;

  packages: TQuotaPackage[] = [];

  /** The one package of this purchase, or null until the author picks one. */
  selectedProductId: number | null = null;

  /** The company to invoice: the company field, with its lookups, its resolved company and its lock. */
  readonly companyField: CompanyFieldStore;

  invoiceEmail = '';

  /**
   * The invoice email the resolved company has on file, when it has members and the owner is not one of
   * them: the purchase is invoiced there whatever is typed, so the form shows it instead of the field.
   * Asked of the backend each time the company field resolves to a company it is not locked to; null
   * otherwise, and until the answer comes.
   */
  invoiceEmailOnFile: string | null = null;

  isSubmitting = false;

  /** Why the last purchase did not go through, or null. The form stays submittable. */
  purchaseFailureMessage: string | null = null;

  /** Set once a purchase stands; the form is done then. */
  confirmation: IPurchaseConfirmation | null = null;

  /**
   * Whether the person signed in may buy, as the purchase form last said (`mayBuy`); null until it has
   * been read. A trial account, or one without a role, may not: the backend decides from the role and
   * refuses a purchase all the same. Kept across opening and closing the form; forgotten with the owner.
   */
  mayBuy: boolean | null = null;

  private deps: IPurchaseStoreDependencies;

  /** The open form's idempotency key, sent with every buy from it; null while the form is closed. */
  private idempotencyKey: string | null = null;

  /** Raised by every open and close. An answer lands only in the opening it was asked for. */
  private formGeneration = 0;

  /** Raised by every `forget`. Whether someone may buy lands only for the person it was asked for. */
  private ownerGeneration = 0;

  constructor(dependencies: IPurchaseStoreDependencies) {
    this.deps = dependencies;
    this.companyField = dependencies.companyField;

    makeAutoObservable<this, 'deps' | 'idempotencyKey' | 'formGeneration' | 'ownerGeneration'>(this, {
      deps: false,
      idempotencyKey: false,
      formGeneration: false,
      ownerGeneration: false,
      companyField: false,
    });

    // The store lives as long as the app, so the reaction is never disposed.
    reaction(
      () => (this.isOpen && !this.companyField.isLocked ? (this.companyField.resolvedCompany?.orgNumber ?? null) : null),
      (orgNumber) => void this.readInvoiceEmailOnFile(orgNumber),
    );
  }

  get selectedPackage(): TQuotaPackage | null {
    return this.packages.find((aPackage) => aPackage.productId === this.selectedProductId) ?? null;
  }

  /** Where the invoice goes: the company's address on file when it has one for this owner, else what is typed. */
  get invoiceEmailToSend(): string {
    return this.invoiceEmailOnFile ?? this.invoiceEmail.trim();
  }

  /** Why the invoice email cannot be sent as it is, or null while it is empty, on file, or reads as an email address. */
  get invoiceEmailError(): string | null {
    const email = this.invoiceEmailToSend;
    return email === '' || SIMPLE_EMAIL_PATTERN.test(email) ? null : INVALID_INVOICE_EMAIL_MESSAGE;
  }

  /** A package, a registered company and an invoice email that reads as one, and nothing on its way. */
  get canSubmit(): boolean {
    return this.isOpen && !this.isLoading && !this.isSubmitting && this.confirmation === null && this.selectedPackage !== null && this.companyField.resolvedCompany !== null && this.invoiceEmailToSend !== '' && this.invoiceEmailError === null;
  }

  /** Opens the form and reads what it opens with: the packages on sale and the details to prefill. */
  open = async (): Promise<void> => {
    this.reset();
    const generation = this.formGeneration;
    const ownerGeneration = this.ownerGeneration;
    this.isOpen = true;
    this.isLoading = true;
    this.idempotencyKey = this.deps.newKey();
    try {
      const form = await this.deps.agent.QuotaPackages.getPurchaseForm();
      runInAction(() => {
        if (ownerGeneration === this.ownerGeneration) this.mayBuy = form.mayBuy !== false;
        if (generation !== this.formGeneration) return;
        this.packages = form.packages;
        this.invoiceEmail = form.prefill.invoiceEmail ?? '';
        this.isLoading = false;
        const { orgNumber, companyName, companyLocked } = form.prefill;
        if (orgNumber && companyName) {
          // The company of the latest accepted purchase, as the register named it then: resolved, with no
          // lookup. Locked, it shows grouped as a number the author reads rather than edits.
          this.companyField.prefill({ orgNumber, name: companyName }, companyLocked === true);
        } else if (orgNumber) {
          this.companyField.setQuery(orgNumber);
        }
      });
    } catch (error) {
      runInAction(() => {
        if (generation !== this.formGeneration) return;
        this.loadFailureMessage = resolveErrorMessage(error, FORM_UNAVAILABLE_MESSAGE);
        this.isLoading = false;
      });
    }
  };

  /** Closes the form and forgets what was typed. Sends nothing. */
  close = (): void => {
    this.reset();
  };

  /**
   * Reads whether the person signed in may buy, from the purchase form, unless it is known already. The
   * balance indicator asks as its breakdown opens, so that it can offer the buy action or say purchases
   * are available on a paid plan. A form that cannot be read leaves it unknown, to be asked again.
   */
  readMayBuy = async (): Promise<void> => {
    if (this.mayBuy !== null) return;
    const ownerGeneration = this.ownerGeneration;
    try {
      const form = await this.deps.agent.QuotaPackages.getPurchaseForm();
      runInAction(() => {
        if (ownerGeneration === this.ownerGeneration) this.mayBuy = form.mayBuy !== false;
      });
    } catch {
      // Unknown: the indicator offers nothing until a later read answers.
    }
  };

  /** Closes the form and forgets whether the person signed in may buy: the owner or the person has changed. */
  forget = (): void => {
    this.reset();
    this.ownerGeneration += 1;
    this.mayBuy = null;
  };

  /**
   * Asks the form again for the company the field now stands for, and holds the invoice email it has on
   * file, if any. An answer about a company the field no longer stands for, or from a form since closed,
   * is dropped; a form that cannot be read leaves the typed address in use, which the backend overrides
   * all the same.
   */
  private readInvoiceEmailOnFile = async (orgNumber: string | null): Promise<void> => {
    this.invoiceEmailOnFile = null;
    if (orgNumber === null) return;
    const generation = this.formGeneration;
    try {
      const form = await this.deps.agent.QuotaPackages.getPurchaseForm(orgNumber);
      runInAction(() => {
        if (generation !== this.formGeneration || this.companyField.resolvedCompany?.orgNumber !== orgNumber) return;
        this.invoiceEmailOnFile = form.prefill.invoiceEmailOnFile ?? null;
      });
    } catch {
      // Not known: the field stays, and the backend invoices to the address on file regardless.
    }
  };

  selectPackage = (productId: number): void => {
    this.selectedProductId = productId;
  };

  setInvoiceEmail = (value: string): void => {
    this.invoiceEmail = value;
  };

  /**
   * Buys the selected package for the resolved company: its organisation number goes, its name does
   * not — the backend takes the register's. When the purchase stands, its balance goes into the
   * balance store and the confirmation shows; when it is refused, the backend's words show and the
   * form can be submitted again, as a new purchase; when no answer comes, the form says the order could
   * not be confirmed, and can be submitted again as the same purchase.
   */
  submit = async (): Promise<void> => {
    const selected = this.selectedPackage;
    const company = this.companyField.resolvedCompany;
    const idempotencyKey = this.idempotencyKey;
    if (!this.canSubmit || selected === null || company === null || idempotencyKey === null) return;

    this.purchaseFailureMessage = null;
    const generation = this.formGeneration;
    this.isSubmitting = true;
    try {
      const response = await this.deps.agent.QuotaPackages.buy({
        productId: selected.productId,
        orgNumber: company.orgNumber,
        invoiceEmail: this.invoiceEmailToSend,
        idempotencyKey,
      });
      runInAction(() => {
        // The credits are the author's whether or not the form is still open.
        if (response.balance) this.deps.acceptBalance(response.balance);
        if (generation !== this.formGeneration) return;
        const isPending = response.outcome === 'pending';
        this.isSubmitting = false;
        this.confirmation = {
          productName: selected.productName,
          credits: response.credits,
          isPending,
          message: isPending ? response.message || ORDER_REGISTERED_MESSAGE : null,
          totalCreditsAfter: response.balance ? response.balance.monthly + response.balance.purchased : null,
        };
      });
    } catch (error) {
      runInAction(() => {
        if (generation !== this.formGeneration) return;
        this.isSubmitting = false;
        if (wasAnswered(error)) {
          // Refused: whatever was sent is settled, and buying again is a new purchase.
          this.idempotencyKey = this.deps.newKey();
          this.purchaseFailureMessage = resolveErrorMessage(error, PURCHASE_FAILED_MESSAGE);
        } else {
          // No answer: the purchase may stand. The key stays, so a second click is the same purchase.
          this.purchaseFailureMessage = PURCHASE_NOT_CONFIRMED_MESSAGE;
        }
      });
    }
  };

  private reset(): void {
    this.formGeneration += 1;
    this.idempotencyKey = null;
    this.isOpen = false;
    this.isLoading = false;
    this.loadFailureMessage = null;
    this.packages = [];
    this.selectedProductId = null;
    this.companyField.reset();
    this.invoiceEmail = '';
    this.invoiceEmailOnFile = null;
    this.isSubmitting = false;
    this.purchaseFailureMessage = null;
    this.confirmation = null;
  }
}
